import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, Info } from "lucide-react";
import ProjectInfoView from "./ProjectInfoView";
import ProjectNavigation from "../../layout/ProjectNavigation";
import ProjectDashboardView from "./ProjectDashboardView";
import InspectionsView from "./inspections/InspectionsView";
import EquipmentView from "./EquipmentView";
import NCRView from "./NCRView";
import RFIView from "./RFIView";
import SafetyView from "./SafetyView";
import DailyReportsView from "./DailyReportsView";
import WeeklyReportsView from "./WeeklyReportsView";
import DocumentsView from "./DocumentsView";
import PdfSplitterView from "./pdf-splitter/PdfSplitterView";
import KnowledgeBaseIntakeView from "./KnowledgeBaseIntakeView";
import PhotosView from "./PhotosView";
import MaterialsView from "./MaterialsView";
import SubmittalsView from "./SubmittalsView";
import AnalyticsView from "./AnalyticsView";
import ProjectTeamView from "./ProjectTeamView";
import { useProjectContext } from "../../../hooks/useProjectContext";
import ProjectRagChatWidget from "./ProjectRagChatWidget";
import InspectionTemplatesView from "./InspectionTemplatesView";
import InternalRFIView from "./InternalRFIView";
import InternalSubmittalsView from "./InternalSubmittalsView";
import AssignedFindingsView from "./AssignedFindingsView";
import AssignedInspectionsView from "./AssignedInspectionsView";
import WalkSchedulerView from "./WalkSchedulerView";
import SupervisorDashboardView from "./SupervisorDashboardView";
import SupervisorGraphicalDashboardView from "./SupervisorGraphicalDashboardView";
import MyIssuesView from "./MyIssuesView";
import EngineeringIssuesView from "./EngineeringIssuesView";
import OperationsManagerDashboardView from "./OperationsManagerDashboardView";
import ProjectAttentionCenter from "./ProjectAttentionCenter";
const ProjectLayout = ({
  currentUser,
  activeProject,
  activeProjectTab,
  setActiveProjectTab,
  projectRole,
}) => {
  const { setActiveProject, clearProject } = useProjectContext();
  const [openInspectionContextId, setOpenInspectionContextId] = useState(null);
  const [openEquipmentContextId, setOpenEquipmentContextId] = useState(null);
  const [openEquipmentTaskContextId, setOpenEquipmentTaskContextId] = useState(null);
  const isThirdParty = (projectRole || "").toLowerCase() === "third_party";

  useEffect(() => {
    setActiveProject(activeProject || null);
  }, [activeProject, setActiveProject]);

  // Third-party testing agencies do not use the regular project dashboard.
  // Once their project role resolves, land them directly in Equipment.
  useEffect(() => {
    if (isThirdParty && !["equipment", "walk-scheduler", "pdf-splitter"].includes(activeProjectTab)) {
      setActiveProjectTab("equipment");
    }
  }, [isThirdParty, activeProjectTab, setActiveProjectTab]);

  const navigateProjectTab = useCallback(
    (tab, detail = {}) => {
      if (detail?.inspectionId) {
        setOpenInspectionContextId(Number(detail.inspectionId));
      }
      if (detail?.equipmentId) {
        setOpenEquipmentContextId(Number(detail.equipmentId));
      }
      if (detail?.equipmentTaskId || detail?.taskId) {
        setOpenEquipmentTaskContextId(
          Number(detail.equipmentTaskId || detail.taskId),
        );
      }
      setActiveProjectTab(tab);
    },
    [setActiveProjectTab],
  );

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (!detail.tab) return;
      navigateProjectTab(detail.tab, detail);
    };
    window.addEventListener("smokyq:navigate-project-tab", handler);
    return () =>
      window.removeEventListener("smokyq:navigate-project-tab", handler);
  }, [navigateProjectTab]);

  if (!activeProject) {
    return (
      <div className="p-8 text-center text-gray-500">No project selected</div>
    );
  }

  const renderProjectView = () => {
    switch (activeProjectTab) {
      case "dashboard": {
        const role = (projectRole || "").toLowerCase();
        if (role === "operations_manager") {
          return <OperationsManagerDashboardView onNavigate={navigateProjectTab} dashboardMode />;
        }
        if (["supervisor", "general_foreman"].includes(role)) {
          return (
            <SupervisorGraphicalDashboardView
              onNavigate={navigateProjectTab}
              projectRole={projectRole}
            />
          );
        }
        return <ProjectDashboardView onNavigate={navigateProjectTab} />;
      }
      case "operations-performance":
        return <OperationsManagerDashboardView onNavigate={navigateProjectTab} />;
      case "engineering-issues":
        return <EngineeringIssuesView />;
      case "info":
        return (
          <ProjectInfoView
            currentUser={currentUser}
            projectRole={projectRole}
          />
        );
      // case "assigned-findings":
      //   return <AssignedFindingsView />;
      case "my-issues":
        return <MyIssuesView onNavigate={navigateProjectTab} />;
      case "assigned-inspections":
        return (
          <AssignedInspectionsView
            openInspectionId={openInspectionContextId}
            onOpenedInspection={() => setOpenInspectionContextId(null)}
          />
        );
      case "walk-scheduler":
        return <WalkSchedulerView />;
      case "issues-control":
        return <SupervisorDashboardView projectRole={projectRole} />;
      case "inspections":
        return <InspectionsView />;
      case "equipment":
        return (
          <EquipmentView
            openEquipmentId={openEquipmentContextId}
            openTaskId={openEquipmentTaskContextId}
            onOpenedContext={() => {
              setOpenEquipmentContextId(null);
              setOpenEquipmentTaskContextId(null);
            }}
          />
        );
      case "inspection-templates":
        return <InspectionTemplatesView />;

      case "ncr":
        return <NCRView />;

      case "rfi":
        return <RFIView />;

      case "safety":
        return <SafetyView />;

      case "daily":
        return <DailyReportsView />;

      case "weekly":
        return <WeeklyReportsView />;

      case "documents":
        return <DocumentsView />;
      case "pdf-splitter":
        return <PdfSplitterView />;

      case "kb-intake":
        return <KnowledgeBaseIntakeView />;

      case "photos":
        return <PhotosView />;

      case "materials":
        return <MaterialsView />;

      case "submittals":
        return <SubmittalsView />;

      case "analytics":
        return <AnalyticsView />;
      case "internal-rfi":
        return <InternalRFIView />;
      case "internal-submittals":
        return <InternalSubmittalsView />;
      case "team":
        return (
          <ProjectTeamView
            projectId={activeProject.id}
            systemRole={currentUser?.role}
            projectRole={projectRole}
          />
        );

      default:
        return <div className="p-6 text-gray-500">Select a project module</div>;
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Back button (works for admin + non-admin) */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              clearProject();
              setActiveProjectTab((projectRole || "").toLowerCase() === "third_party" ? "equipment" : "dashboard");
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <div className="text-sm text-gray-600">{activeProject?.name}</div>
          {!isThirdParty && (
            <button
              onClick={() => setActiveProjectTab("info")}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs text-gray-700"
              title="Project Info"
            >
              <Info className="w-3.5 h-3.5" />
              Info
            </button>
          )}
        </div>

        <ProjectNavigation
          systemRole={currentUser?.role}
          projectRole={projectRole}
          activeProject={activeProject}
          activeTab={activeProjectTab}
          onTabChange={navigateProjectTab}
        />

        {/* Project Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {renderProjectView()}
        </div>
      </div>
      {!isThirdParty && <ProjectAttentionCenter projectId={activeProject.id} />}
      {/* Floating project RAG chat assistant */}
      {!isThirdParty && <ProjectRagChatWidget />}
    </>
  );
};

export default ProjectLayout;
